import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../features/auth/AuthProvider.js";
import { ApiClientError } from "../services/api-client.js";
import * as usersApi from "../services/users-api.js";
import type { TenantUser, TenantUserRole } from "../services/users-api.js";

/* Roles tenant-scoped que un tenant-admin puede asignar. Las roles plataforma
   (superadmin/platform-support) NO se ofrecen en la UI: la API las rechazaría
   con 403, así que las ocultamos para mejor UX. */
const TENANT_ROLE_OPTIONS: TenantUserRole[] = [
  "tenant-admin",
  "warehouse-supervisor",
  "warehouse-operator",
  "finance",
  "customer-portal-user",
  "auditor",
];

type FormState = {
  email: string;
  password: string;
  role: TenantUserRole;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  email: "",
  password: "",
  role: "warehouse-operator",
  isActive: true,
};

function isPlatformRole(role: string): boolean {
  return role === "superadmin" || role === "platform-support";
}

function canManageUsers(role: string): boolean {
  return isPlatformRole(role) || role === "tenant-admin";
}

export function UsersPage(): JSX.Element {
  const { t } = useTranslation();
  const { token, user } = useAuth();

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ok" | "error" | "forbidden">("idle");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const isAllowed = !!user && canManageUsers(user.role);

  const tenantQuery = useMemo(() => {
    if (user?.tenantId) return { tenantId: user.tenantId };
    return {};
  }, [user?.tenantId]);

  const friendlyError = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiClientError) {
        if (e.status === 401) return t("errors.unauthorized");
        if (e.status === 403) return t("errors.forbidden");
        if (e.status === 404) return t("errors.notFound");
        if (e.status === 409) return e.message;
        if (e.status >= 400 && e.status < 500) return e.message || t("errors.badRequest");
        return e.message || t("errors.generic");
      }
      return t("errors.generic");
    },
    [t],
  );

  const load = useCallback(async () => {
    if (!token || !isAllowed) return;
    setLoadState("loading");
    setErrorMsg(null);
    try {
      const { users: rows } = await usersApi.listTenantUsers(token, tenantQuery);
      setUsers(rows);
      setLoadState("ok");
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 403) {
        setLoadState("forbidden");
      } else {
        setLoadState("error");
      }
      setErrorMsg(friendlyError(e));
    }
  }, [token, isAllowed, tenantQuery, friendlyError]);

  useEffect(() => {
    void load();
  }, [load]);

  const startCreate = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowCreate(true);
  }, []);

  const startEdit = useCallback((row: TenantUser) => {
    setShowCreate(false);
    setEditingId(row.id);
    const safeRole = (TENANT_ROLE_OPTIONS as string[]).includes(row.role)
      ? (row.role as TenantUserRole)
      : "warehouse-operator";
    setForm({
      email: row.email,
      password: "",
      role: safeRole,
      isActive: row.isActive,
    });
    setFormError(null);
  }, []);

  const cancelForm = useCallback(() => {
    setShowCreate(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!token) return;
    setFormError(null);
    setSubmitting(true);
    try {
      if (editingId) {
        const payload: usersApi.TenantUserUpdateInput = {
          email: form.email.trim(),
          role: form.role,
          isActive: form.isActive,
        };
        if (form.password.trim()) payload.password = form.password;
        const { user: updated } = await usersApi.updateTenantUserRequest(token, editingId, payload);
        setUsers((prev) => prev.map((u) => (u.id === editingId ? updated : u)));
        cancelForm();
      } else {
        if (!form.password.trim()) {
          setFormError(t("portal.users.passwordRequired"));
          setSubmitting(false);
          return;
        }
        const payload: usersApi.TenantUserCreateInput = {
          email: form.email.trim(),
          password: form.password,
          role: form.role,
        };
        const { user: created } = await usersApi.createTenantUserRequest(token, payload);
        setUsers((prev) => [created, ...prev]);
        cancelForm();
      }
    } catch (e) {
      setFormError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  }, [token, editingId, form, friendlyError, cancelForm, t]);

  const handleDelete = useCallback(
    async (row: TenantUser) => {
      if (!token) return;
      if (row.id === user?.id) {
        setErrorMsg(t("portal.users.cannotSelfDelete"));
        return;
      }
      const message = t("common.confirmDelete", { name: row.email });
      if (!window.confirm(message)) return;
      try {
        await usersApi.deleteTenantUserRequest(token, row.id);
        setUsers((prev) => prev.map((u) => (u.id === row.id ? { ...u, isActive: false } : u)));
      } catch (e) {
        setErrorMsg(friendlyError(e));
      }
    },
    [token, user?.id, t, friendlyError],
  );

  if (!isAllowed) {
    return (
      <div className="users-page">
        <header className="page-header">
          <h1>{t("portal.users.title")}</h1>
        </header>
        <p className="banner-inline" role="note">
          {t("portal.users.notAllowed")}
        </p>
      </div>
    );
  }

  return (
    <div className="users-page">
      <header className="page-header">
        <h1>{t("portal.users.title")}</h1>
        <p className="text-muted">{t("portal.users.subtitle")}</p>
      </header>

      {errorMsg ? <p className="form-error">{errorMsg}</p> : null}

      {!showCreate && editingId == null ? (
        <div className="actions-row">
          <button type="button" className="btn btn-primary" onClick={startCreate}>
            {t("portal.users.new")}
          </button>
        </div>
      ) : null}

      {(showCreate || editingId != null) ? (
        <section className="card create-order-card">
          <h2 className="section-title">
            {editingId ? t("portal.users.edit") : t("portal.users.new")}
          </h2>
          {formError ? <p className="form-error">{formError}</p> : null}
          <div className="filters-row">
            <label className="field-inline field-grow">
              <span>{t("portal.users.email")}</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                maxLength={320}
                required
              />
            </label>
            <label className="field-inline field-grow">
              <span>
                {t("portal.users.password")}{" "}
                {editingId ? (
                  <small className="text-muted">{t("portal.users.passwordEditHint")}</small>
                ) : null}
              </span>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                minLength={editingId ? 0 : 8}
                maxLength={256}
                placeholder={editingId ? "********" : ""}
              />
            </label>
            <label className="field-inline">
              <span>{t("portal.users.role")}</span>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as TenantUserRole }))}
              >
                {TENANT_ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {t(`portal.users.roles.${r}`, { defaultValue: r })}
                  </option>
                ))}
              </select>
            </label>
            <label className="field-inline">
              <span>{t("common.status")}</span>
              <select
                value={form.isActive ? "active" : "inactive"}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === "active" }))}
              >
                <option value="active">{t("common.active")}</option>
                <option value="inactive">{t("common.inactive")}</option>
              </select>
            </label>
          </div>
          <div className="status-actions">
            <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={submitting}>
              {submitting ? t("common.loading") : t("common.save")}
            </button>
            <button type="button" className="btn" onClick={cancelForm} disabled={submitting}>
              {t("common.cancel")}
            </button>
          </div>
        </section>
      ) : null}

      {loadState === "loading" ? <p className="text-muted">{t("common.loading")}</p> : null}

      {loadState === "forbidden" ? (
        <p className="banner-inline" role="note">
          {t("portal.users.notAllowed")}
        </p>
      ) : null}

      {loadState === "ok" ? (
        <section className="card table-section">
          <h2 className="section-title">
            {t("portal.users.list")} <span className="text-muted small">({users.length})</span>
          </h2>
          {users.length === 0 ? (
            <p className="text-muted empty-hint">{t("common.noResults")}</p>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t("portal.users.email")}</th>
                    <th>{t("portal.users.role")}</th>
                    <th>{t("common.status")}</th>
                    <th>{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.email}</td>
                      <td>
                        <code>{u.role}</code>
                      </td>
                      <td>
                        <span className={u.isActive ? "badge-active" : "badge-inactive"}>
                          {u.isActive ? t("common.active") : t("common.inactive")}
                        </span>
                      </td>
                      <td className="nowrap">
                        <button type="button" className="btn btn-sm" onClick={() => startEdit(u)}>
                          {t("common.edit")}
                        </button>{" "}
                        {u.isActive && u.id !== user?.id ? (
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => void handleDelete(u)}>
                            {t("common.delete")}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
